import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  MoreOutlined
} from "@ant-design/icons"
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Image,
  Popconfirm,
  Progress,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  type UploadFile
} from "antd"
import { type MenuItemType } from "antd/es/menu/interface"
import React, { memo, useMemo, useState } from "react"

import { showFileAction } from "../../../../utils/documents/show-file-action.ts"
import { downloadFile } from "../../../../utils/download-file.ts"
import { type Attachment } from "../../../Album/types/attachment.types.ts"
import { AttachmentTags } from "../constants/attachment-tags.tsx"
import { getFileIcon, isImage } from "../helpers/file.tsx"
import { RenameFileModal } from "./RenameFileModal.tsx"

const { Text } = Typography

interface ProjectFileCardProps {
  currentUserId: string
  file: UploadFile<Attachment>
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  editAllowed: boolean
  editForeignAllowed: boolean
  deleting?: boolean
  onPreviewFile: (file: UploadFile) => void
  // onCancelUpload?: (uid: string) => void
  onDelete?: (attachmentId: string) => void
  onRename?: (attachmentId: string, nextFileName: string) => Promise<void>
  onAddToAlbum?: (fileId: string) => void
  showAddtoAlbum?: boolean
}

export const ProjectFileCard = memo(
  ({
    file,
    currentUserId,
    deleteAllowed,
    deleteForeignAllowed,
    editAllowed,
    editForeignAllowed,
    deleting = false,
    onPreviewFile,
    // onCancelUpload,
    onDelete,
    onRename,
    onAddToAlbum,
    showAddtoAlbum
  }: ProjectFileCardProps) => {
    const [previewImage, setPreviewImage] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [actionsOpen, setActionsOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [renameOpen, setRenameOpen] = useState(false)

    const isUploading = file.status === "uploading"
    const isError = file.status === "error"
    const attachment = file.response

    const mimeType = attachment?.mime_type || file.type || ""
    const isImageType = isImage(mimeType)
    const fileName = attachment?.original_filename || file.name
    const fileSize = attachment?.formatted_file_size
    const fileUrl = attachment?.file || file.url

    const showDelete = useMemo(() => {
      if (!attachment) return false
      return showFileAction({
        file: attachment,
        allowed: deleteAllowed,
        foreignAllowed: deleteForeignAllowed,
        currentUserId
      })
    }, [currentUserId, deleteAllowed, deleteForeignAllowed, attachment])

    const showRename = useMemo(() => {
      if (!attachment) return false
      return showFileAction({
        file: attachment,
        allowed: editAllowed,
        foreignAllowed: editForeignAllowed,
        currentUserId
      })
    }, [currentUserId, editAllowed, editForeignAllowed, attachment])

    const onDownloadHandler = async () => {
      if (!fileUrl) return

      try {
        setDownloading(true)
        await downloadFile(fileUrl, fileName)
      } catch (error) {
        console.error("Failed to download file:", error)
      } finally {
        setDownloading(false)
      }
    }

    // const handleCancelUpload = () => {
    //   onCancelUpload?.(file.uid)
    // }

    // Uploading state
    if (isUploading) {
      return (
        <Card
          hoverable
          className="relative size-full"
          styles={{ body: { padding: 8, height: "100%" } }}
        >
          <Flex
            vertical
            align="center"
            justify="center"
            gap={12}
            className="h-full"
          >
            <Progress
              type="circle"
              percent={file.percent || 0}
              size={80}
              status="active"
            />
            <Text
              ellipsis={{ tooltip: fileName }}
              className="text-xs max-w-full"
            >
              {fileName}
            </Text>
            {/*<Button*/}
            {/*  size="small"*/}
            {/*  icon={<CloseOutlined />}*/}
            {/*  onClick={handleCancelUpload}*/}
            {/*  danger*/}
            {/*>*/}
            {/*  Cancel*/}
            {/*</Button>*/}
          </Flex>
        </Card>
      )
    }

    // Error state
    if (isError) {
      return (
        <Card
          hoverable
          className="relative border-red-300"
          styles={{ body: { padding: 8, height: "100%" } }}
        >
          <Flex
            vertical
            align="center"
            justify="center"
            gap={12}
            className="h-full"
          >
            <div className="text-4xl">❌</div>
            <Text type="danger" className="text-xs">
              Upload failed
            </Text>
            <Text
              ellipsis={{ tooltip: fileName }}
              className="text-xs max-w-full"
              type="secondary"
            >
              {fileName}
            </Text>
          </Flex>
        </Card>
      )
    }

    // Normal state (uploaded file)
    return (
      <>
        {renameOpen && attachment?.id && (
          <RenameFileModal
            fileName={fileName}
            onClose={() => setRenameOpen(false)}
            onSubmit={(next) => onRename!(attachment.id, next)}
          />
        )}
        <Card
          hoverable
          className={`relative size-full flex flex-col ${deleting ? "opacity-50 pointer-events-none" : ""}`}
          classNames={{
            body: "p-2 flex-1",
            cover: "border rounded-t-card overflow-hidden border-border"
          }}
          cover={
            <div className={"relative group  "}>
              <Flex
                onClick={() => {
                  if (isImageType) setPreviewImage(true)
                  else onPreviewFile(file)
                }}
                justify={"center"}
                align={"center"}
                className={`absolute z-10 inset-0 bg-black/50 group-hover:opacity-100 transition-all duration-500 ${
                  actionsOpen ? "opacity-100" : "opacity-0"
                }`}
              >
                <div
                  className="absolute top-1 right-3 "
                  onClick={(e) => e.stopPropagation()}
                >
                  <Dropdown
                    trigger={["click"]}
                    open={actionsOpen || deleteConfirmOpen}
                    onOpenChange={setActionsOpen}
                    menu={{
                      items: [
                        {
                          key: "preview",
                          className: "p-0",
                          label: (
                            <Button
                              className="w-full flex justify-start"
                              type="text"
                              loading={downloading}
                              iconPosition="start"
                              icon={<DownloadOutlined />}
                              onClick={onDownloadHandler}
                              disabled={!fileUrl || deleting}
                            >
                              Download
                            </Button>
                          )
                        },
                        {
                          key: "rename",
                          className: "p-0",
                          hidden: !onRename || !attachment?.id || !showRename,
                          label: (
                            <Button
                              className="w-full flex justify-start"
                              type="text"
                              iconPosition="start"
                              icon={<EditOutlined />}
                              onClick={() => setRenameOpen(true)}
                              disabled={deleting}
                            >
                              Rename
                            </Button>
                          )
                        },
                        {
                          key: "delete",
                          className: "p-0",
                          hidden: !onDelete || !attachment?.id || !showDelete,
                          label: (
                            <Popconfirm
                              title="Delete file?"
                              description="Are you sure you want to delete this file?"
                              onConfirm={() =>
                                attachment?.id && onDelete?.(attachment.id)
                              }
                              onOpenChange={setDeleteConfirmOpen}
                              okText="Yes"
                              cancelText="No"
                              disabled={deleting}
                            >
                              <Button
                                className="w-full flex justify-start"
                                type="text"
                                danger
                                loading={deleting}
                                iconPosition="start"
                                icon={<DeleteOutlined />}
                                disabled={deleting}
                              >
                                Delete
                              </Button>
                            </Popconfirm>
                          )
                        },
                        {
                          key: "addToAlbum",
                          hidden:
                            !onAddToAlbum || !attachment?.id || !showAddtoAlbum,
                          className: "p-0",
                          label: (
                            <Button
                              className="w-full flex justify-start"
                              type="text"
                              iconPosition="start"
                              icon={<FileAddOutlined />}
                              onClick={() =>
                                attachment?.id && onAddToAlbum?.(attachment?.id)
                              }
                            >
                              Add To Album
                            </Button>
                          )
                        }
                      ] as MenuItemType[]
                    }}
                  >
                    <Button icon={<MoreOutlined />} />
                  </Dropdown>
                </div>
                <Space className={"text-white text-shadow-lg font-bold"}>
                  <EyeOutlined className={"text-lg"} />
                  Preview
                </Space>
              </Flex>
              {isImageType && fileUrl ? (
                <div className="aspect-square overflow-hidden bg-primary/10">
                  <Image
                    loading={"lazy"}
                    rootClassName="size-full"
                    alt={fileName}
                    src={fileUrl}
                    className="object-cover size-full"
                    preview={{
                      mask: null,
                      visible: previewImage,
                      src: fileUrl,
                      onVisibleChange: (value) => !value && setPreviewImage(value) // only for close
                    }}
                    placeholder={<Skeleton.Node className={"size-full"} />}
                  />
                </div>
              ) : (
                <div className="aspect-square bg-primary/10 flex items-center justify-center">
                  <div className="[&_svg]:text-5xl">{getFileIcon(file.type)}</div>
                </div>
              )}
            </div>
          }
        >
          <div className="flex flex-col  h-full gap-1">
            <Flex className="flex">
              <Text ellipsis={{ tooltip: fileName }} className="text-xs" strong>
                {fileName}
              </Text>
              {fileSize && (
                <Text type="secondary" className="text-xs shrink-0">
                  ({fileSize})
                </Text>
              )}
            </Flex>

            <Flex vertical className={"gap-y-1 flex-1 h-full"}>
              {/*{attachment?.created_by_name && (*/}
              {/*  <Flex gap={3}>*/}
              {/*    <Text type="secondary" className="text-xs shrink-0">*/}
              {/*      Uploaded by:*/}
              {/*    </Text>*/}
              {/*    <Text*/}
              {/*      ellipsis={{ tooltip: attachment.created_by_name }}*/}
              {/*      type="secondary"*/}
              {/*      className="text-xs"*/}
              {/*    >*/}
              {/*      {attachment.created_by_name}*/}
              {/*    </Text>*/}
              {/*  </Flex>*/}
              {/*)}*/}

              {attachment?.created && (
                <Text type="secondary" className="text-xs">
                  {attachment.created}
                </Text>
              )}

              {attachment?.model_type_display &&
                attachment?.related_object_name && (
                  <Tooltip title={attachment?.model_type_display}>
                    <Tag
                      className={"inline-flex w-fit max-w-full truncate"}
                      color={AttachmentTags?.[attachment?.model_type_display]}
                    >
                      <Text
                        className={"text-inherit text-xs"}
                        ellipsis={{
                          tooltip: {
                            placement: "bottom",
                            title: attachment?.related_object_name
                          }
                        }}
                      >
                        {attachment?.related_object_name}
                      </Text>
                    </Tag>
                  </Tooltip>
                )}

              {attachment?.created_by_name && (
                <Tooltip title={`Uploaded by: ${attachment?.created_by_name}`}>
                  <Tag
                    className={"inline-flex w-fit max-w-full truncate"}
                    color={"blue"}
                  >
                    <Text className={"text-inherit text-xs"} ellipsis>
                      {attachment?.created_by_name}
                    </Text>
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          </div>
        </Card>
      </>
    )
  }
)

ProjectFileCard.displayName = "ProjectFileCard"
